import { useState, useEffect, useMemo } from 'react';
import { Modal, Box, Typography, IconButton, Chip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';

const isVideoUrl = (url) => /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(url || '');

const extractMediaUrl = (item) => {
    if (!item) return null;
    if (typeof item === 'string') return item.trim() || null;
    return item.url || item.videoUrl || item.video || item.path || item.location || item.src || null;
};

const isVideoMediaItem = (item, url) => {
    if (item && typeof item === 'object') {
        const type = String(item.type || item.mediaType || item.mimeType || '').toLowerCase();
        if (type.includes('video')) return true;
    }
    return isVideoUrl(url);
};

const normalizeMediaList = (list) => {
    if (!list) return [];
    const items = Array.isArray(list) ? list : [list];
    return items
        .map((item) => {
            const url = extractMediaUrl(item);
            if (!url) return null;
            return {
                url,
                type: isVideoMediaItem(item, url) ? 'video' : 'image',
            };
        })
        .filter(Boolean);
};

const normalizeUrlList = (list) => normalizeMediaList(list).map((item) => item.url);

const getProductImages = (images, imageUrl) => {
    const list = normalizeMediaList(images).filter((item) => item.type === 'image').map((item) => item.url);
    if (list.length > 0) return list;
    if (imageUrl && !isVideoUrl(imageUrl)) return [imageUrl];
    return [];
};

const getProductVideos = (videos) => {
    if (typeof videos === 'string' && videos.trim()) {
        return isVideoUrl(videos) || !/\.(jpe?g|png|gif|webp|bmp|svg)(\?.*)?$/i.test(videos)
            ? [videos]
            : [];
    }
    return normalizeMediaList(videos)
        .filter((item) => item.type === 'video')
        .map((item) => item.url);
};

const getProductImagesFromRow = (row) => {
    const fromImages = normalizeMediaList(row?.images)
        .filter((item) => item.type === 'image')
        .map((item) => item.url);
    if (fromImages.length > 0) return fromImages;

    const fromProductImages = normalizeMediaList(row?.productImages)
        .filter((item) => item.type === 'image')
        .map((item) => item.url);
    if (fromProductImages.length > 0) return fromProductImages;

    if (row?.url && !isVideoUrl(row.url)) return [row.url];
    return [];
};

const getProductVideosFromRow = (row) => {
    const videos = [];
    const addVideo = (url) => {
        if (url && !videos.includes(url)) videos.push(url);
    };

    [row?.videos, row?.productVideos, row?.video, row?.videoUrl].forEach((source) => {
        getProductVideos(source).forEach(addVideo);
    });

    normalizeMediaList(row?.images)
        .filter((item) => item.type === 'video')
        .forEach((item) => addVideo(item.url));

    if (row?.url && isVideoUrl(row.url)) addVideo(row.url);

    return videos;
};

const buildMediaGallery = (images, videos, imageUrl) => {
    const imageItems = getProductImages(images, imageUrl).map((url) => ({
        type: 'image',
        url,
    }));
    const videoItems = getProductVideos(videos).map((url) => ({
        type: 'video',
        url,
    }));
    return [...imageItems, ...videoItems];
};

const ImagePreviewModal = ({
    open,
    handleClose,
    imageUrl,
    images,
    videos = [],
    title = 'Product Media',
    productName = '',
}) => {
    const gallery = useMemo(
        () => buildMediaGallery(images, videos, imageUrl),
        [images, videos, imageUrl]
    );
    const [activeIndex, setActiveIndex] = useState(0);

    useEffect(() => {
        if (open) setActiveIndex(0);
    }, [open, imageUrl, images, videos]);

    const hasMedia = gallery.length > 0;
    const activeItem = gallery[activeIndex];

    const goPrev = () => {
        setActiveIndex((prev) => (prev === 0 ? gallery.length - 1 : prev - 1));
    };

    const goNext = () => {
        setActiveIndex((prev) => (prev === gallery.length - 1 ? 0 : prev + 1));
    };

    return (
        <Modal open={open} onClose={handleClose}>
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: { xs: '94%', sm: 600 },
                    maxHeight: '90vh',
                    bgcolor: '#fff',
                    borderRadius: 3,
                    boxShadow: '0 24px 48px rgba(15, 23, 42, 0.2)',
                    overflow: 'hidden',
                    outline: 'none',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 2.5,
                        py: 2,
                        background: 'linear-gradient(135deg, #752ADA 0%, #9b5de5 100%)',
                        color: '#fff',
                    }}
                >
                    <Box>
                        <Typography variant="subtitle1" fontWeight={700}>
                            {title}
                        </Typography>
                        {productName && (
                            <Typography variant="caption" sx={{ opacity: 0.9 }}>
                                {productName}
                            </Typography>
                        )}
                    </Box>
                    <IconButton onClick={handleClose} sx={{ color: '#fff' }} aria-label="close">
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box sx={{ p: 2.5, overflowY: 'auto', maxHeight: 'calc(90vh - 72px)' }}>
                    {hasMedia ? (
                        <>
                            <Box
                                sx={{
                                    position: 'relative',
                                    borderRadius: 2,
                                    bgcolor: '#0f172a',
                                    border: '1px solid #e2e8f0',
                                    minHeight: 300,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                }}
                            >
                                {gallery.length > 1 && (
                                    <>
                                        <IconButton
                                            onClick={goPrev}
                                            sx={{
                                                position: 'absolute',
                                                left: 8,
                                                zIndex: 2,
                                                bgcolor: 'rgba(255,255,255,0.9)',
                                                '&:hover': { bgcolor: '#fff' },
                                            }}
                                            size="small"
                                        >
                                            <ChevronLeftIcon />
                                        </IconButton>
                                        <IconButton
                                            onClick={goNext}
                                            sx={{
                                                position: 'absolute',
                                                right: 8,
                                                zIndex: 2,
                                                bgcolor: 'rgba(255,255,255,0.9)',
                                                '&:hover': { bgcolor: '#fff' },
                                            }}
                                            size="small"
                                        >
                                            <ChevronRightIcon />
                                        </IconButton>
                                    </>
                                )}

                                {activeItem?.type === 'video' || isVideoUrl(activeItem?.url) ? (
                                    <Box
                                        component="video"
                                        key={activeItem.url}
                                        src={activeItem.url}
                                        controls
                                        preload="metadata"
                                        playsInline
                                        sx={{
                                            width: '100%',
                                            maxHeight: 380,
                                            objectFit: 'contain',
                                            bgcolor: '#000',
                                        }}
                                    />
                                ) : (
                                    <Box
                                        component="img"
                                        src={activeItem?.url}
                                        alt={productName || 'Product'}
                                        sx={{
                                            width: '100%',
                                            maxHeight: 380,
                                            objectFit: 'contain',
                                            p: 1,
                                            bgcolor: '#f8fafc',
                                        }}
                                    />
                                )}
                            </Box>

                            {gallery.length > 1 && (
                                <Box sx={{ display: 'flex', gap: 1, mt: 2, overflowX: 'auto', pb: 0.5 }}>
                                    {gallery.map((item, index) => {
                                        const isVideo = item.type === 'video' || isVideoUrl(item.url);
                                        return (
                                            <Box
                                                key={`${item.url}-${index}`}
                                                onClick={() => setActiveIndex(index)}
                                                sx={{
                                                    width: 72,
                                                    height: 72,
                                                    borderRadius: 1.5,
                                                    overflow: 'hidden',
                                                    flexShrink: 0,
                                                    cursor: 'pointer',
                                                    position: 'relative',
                                                    border: activeIndex === index
                                                        ? '2px solid #752ADA'
                                                        : '2px solid transparent',
                                                    boxShadow: activeIndex === index
                                                        ? '0 4px 12px rgba(117,42,218,0.25)'
                                                        : 'none',
                                                    bgcolor: isVideo ? '#0f172a' : '#f8fafc',
                                                }}
                                            >
                                                {isVideo ? (
                                                    <>
                                                        <Box
                                                            component="video"
                                                            src={item.url}
                                                            muted
                                                            sx={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover',
                                                                opacity: 0.85,
                                                            }}
                                                        />
                                                        <PlayCircleOutlineIcon
                                                            sx={{
                                                                position: 'absolute',
                                                                top: '50%',
                                                                left: '50%',
                                                                transform: 'translate(-50%, -50%)',
                                                                color: '#fff',
                                                                fontSize: 28,
                                                            }}
                                                        />
                                                    </>
                                                ) : (
                                                    <Box
                                                        component="img"
                                                        src={item.url}
                                                        alt={`Thumbnail ${index + 1}`}
                                                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                    />
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}

                            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                                <Chip
                                    size="small"
                                    label={`${activeIndex + 1} of ${gallery.length}`}
                                    sx={{ bgcolor: 'rgba(117,42,218,0.1)', color: '#752ADA', fontWeight: 600 }}
                                />
                                <Chip
                                    size="small"
                                    icon={
                                        activeItem?.type === 'video' || isVideoUrl(activeItem?.url)
                                            ? <VideocamOutlinedIcon />
                                            : undefined
                                    }
                                    label={
                                        activeItem?.type === 'video' || isVideoUrl(activeItem?.url)
                                            ? 'Video'
                                            : 'Image'
                                    }
                                    sx={{ fontWeight: 600 }}
                                />
                            </Box>
                        </>
                    ) : (
                        <Box
                            sx={{
                                minHeight: 220,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#94a3b8',
                                gap: 1,
                            }}
                        >
                            <ImageNotSupportedOutlinedIcon sx={{ fontSize: 48 }} />
                            <Typography variant="body2">No images or videos available for this product</Typography>
                        </Box>
                    )}
                </Box>
            </Box>
        </Modal>
    );
};

export default ImagePreviewModal;
export {
    getProductVideos,
    getProductVideosFromRow,
    getProductImagesFromRow,
    buildMediaGallery,
    isVideoUrl,
};
